"use client";

import { m as motion, PanInfo, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Github, ExternalLink, Sparkles } from "lucide-react";
import Image from "next/image";
import { useState, useEffect, memo, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useIsMobile } from "@/lib/performance-hooks";
import { LaptopFrame, MockScreenContent } from "@/components/projects/PhoneCard";
import { cn } from "@/lib/utils";

interface ProjectProps {
  id?: string;
  title: string;
  description: string;
  tags: string[];
  link?: string;
  githubUrl?: string;
  liveUrl?: string;
  image?: string;
  images?: string[];
  year: string;
}

const ProjectCardComponent = ({
  id,
  title,
  description,
  tags,
  link,
  githubUrl,
  liveUrl,
  image,
  images,
  year,
}: ProjectProps) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [direction, setDirection] = useState(0);

  const isWebsite = useMemo(() => 
    !tags.some(t => t.toLowerCase().includes('flutter') || t.toLowerCase().includes('native')),
  [tags]);

  const allImages = images || (image ? [image] : []);

  const handlePrevious = useCallback(() => {
    setDirection(-1);
    setCurrentImageIndex(
      (prev) => (prev - 1 + allImages.length) % (allImages.length || 1)
    );
  }, [allImages.length]);

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentImageIndex((prev) => (prev + 1) % (allImages.length || 1));
  }, [allImages.length]);

  const handleDragEnd = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      setIsDragging(false); // Resume timer
      const offset = info.offset.x;
      const velocity = info.velocity.x;

      if (Math.abs(offset) > 50 || Math.abs(velocity) > 500) {
        if (offset > 0 || velocity > 0) {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    },
    [handlePrevious, handleNext]
  );

  const handleDragStart = useCallback(() => {
    setIsDragging(true); // Pause timer
  }, []);

  useEffect(() => {
    // Logic:
    // 1. Agar images nahi hai ya hovered nahi hai -> Return
    // 2. Agar user Drag kar raha hai (Hold kiya hua hai) -> Return (Pause timer)
    if (allImages.length === 0 || !isHovered || isDragging) return;

    const interval = setInterval(() => {
      handleNext();
    }, isWebsite ? 3000 : 2000);

    return () => clearInterval(interval);

    // Dependencies explain:
    // currentImageIndex: Jaise hi image change hogi (manual ya auto), timer reset hoga.
    // isDragging: Drag karte waqt timer pause hoga, chorte hi naya timer start hoga.
  }, [allImages.length, isHovered, isDragging, handleNext, isWebsite]);

  // Website Slide Variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    },
    exit: (direction: number) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    })
  };

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't navigate if user was dragging carousel
      if (isDragging) return;

      // Don't navigate if clicked on image carousel area
      const target = e.target as HTMLElement;
      if (target.closest("[data-carousel]")) return;

      if (id) {
        router.push(`/projects?app=${id}`);
      }
    },
    [id, isDragging, router]
  );

  return (
    <motion.div
      layoutId={id ? `project-${id}` : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -5 }}
      onClick={handleCardClick}
      className="group relative grid grid-cols-1 md:grid-cols-[1fr_1.4fr] lg:grid-cols-[1.3fr_1.9fr] gap-6 md:gap-8 p-5 md:p-10 pb-12 md:pb-10 border border-border/10 rounded-[2rem] md:rounded-[2.5rem] bg-white/[0.03] dark:bg-white/[0.01] hover:bg-white/[0.06] dark:hover:bg-white/[0.03] transition-all duration-700 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.3)] backdrop-blur-md overflow-hidden cursor-pointer h-full min-h-[640px] md:min-h-[500px] lg:min-h-[550px]"
    >
      {/* Background Decorative Text */}
      <div className="absolute top-0 -left-2 md:-left-6 text-[6rem] sm:text-[10rem] md:text-[15rem] font-black text-foreground/[0.02] select-none pointer-events-none leading-none -translate-y-4 md:-translate-y-12 uppercase break-all">
        {tags.some(t => t.toLowerCase().includes('flutter') || t.toLowerCase().includes('native')) ? 'App' : 'Web'}
      </div>

      <div className="flex flex-col relative z-10 h-full order-2 md:order-1">
        {/* Top Info */}
        <div className="flex items-center gap-3 mb-4 md:mb-8 text-left">
           <span className="text-[10px] md:text-[11px] font-bold tracking-[0.2em] text-primary uppercase">
            {year}
          </span>
          <div className="h-px flex-1 bg-border/20" />
          <span className="text-[9px] md:text-[10px] font-medium text-muted-foreground/60 uppercase tracking-widest leading-none">
            {id?.split("-")[1] || "Project"}
          </span>
        </div>

        {/* Title Area */}
        <div className="space-y-1 mb-4 md:mb-8">
          <motion.h3
            className="text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.1]"
            animate={isHovered ? { x: 8 } : { x: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            {title.includes(" - ") ? title.split(" - ")[0] : title}
            {title.includes(" - ") && (
              <span className="block text-[13px] sm:text-lg md:text-2xl font-normal text-muted-foreground/75 mt-1 md:mt-2">
                {title.split(" - ")[1]}
              </span>
            )}
          </motion.h3>
        </div>

        {/* Description */}
        <div className="relative mb-6 md:mb-8 flex-1 min-h-[90px] md:min-h-[100px]">
          <p className="text-[13px] sm:text-sm md:text-base text-muted-foreground/70 leading-relaxed font-medium line-clamp-3">
            {description}
          </p>
          
          <div className="flex items-center gap-2 mt-4 md:mt-6 text-[9px] md:text-[10px] font-bold text-foreground/70 uppercase tracking-[0.2em]">
            <div className="w-4 h-[1px] bg-primary/40" />
            <span>Fullstack & UI Design</span>
          </div>
        </div>

        {/* Actions & Tags */}
        <div className="pt-2 md:pt-4 space-y-6 md:space-y-8">
          {/* Tech stack row - Minimalist icons/tags */}
          <div className="flex flex-wrap gap-x-3 gap-y-2">
            {tags.slice(0, 5).map((tag) => (
              <span
                key={tag}
                className="text-[10px] md:text-[11px] font-bold text-foreground/60 hover:text-primary transition-colors cursor-default"
              >
                #{tag}
              </span>
            ))}
            {tags.length > 5 && (
              <span className="text-[10px] md:text-[11px] font-bold text-foreground/40 leading-none">
                +{tags.length - 5}
              </span>
            )}
          </div>

          {/* Action Links */}
          {(id ||
            (githubUrl && githubUrl.trim() !== "") ||
            (liveUrl && liveUrl.trim() !== "")) && (
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              {id && (
                <a
                  href={`/projects/${id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="group/link flex items-center gap-2 text-[11px] md:text-xs font-bold text-foreground/60 hover:text-foreground transition-colors"
                >
                  <ArrowUpRight className="w-4 h-4" />
                  <span>Case Study</span>
                </a>
              )}
              {githubUrl && githubUrl.trim() !== "" && (
                <a
                  href={githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="group/link flex items-center gap-2 text-[11px] md:text-xs font-bold text-foreground/60 hover:text-foreground transition-colors"
                >
                  <Github className="w-4 h-4" />
                  <span>Source</span>
                </a>
              )}

              {liveUrl && liveUrl.trim() !== "" && (
                <a
                  href={liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 text-[11px] md:text-xs font-bold text-primary transition-all hover:gap-3"
                >
                  <span>Launch Project</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* IMAGE CAROUSEL SECTION */}
      <div
        data-carousel
        className={cn(
          "relative min-h-[320px] sm:min-h-[350px] md:h-full w-full rounded-xl flex items-center justify-center order-1 md:order-2 group overflow-visible",
          isWebsite ? "bg-transparent" : "bg-black/5 dark:bg-white/[0.01] border border-foreground/10 dark:border-white/5"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {isWebsite ? (
          <div className="w-full h-full flex items-center justify-center p-4 relative">
             {/* Background Decorative Element for Web */}
             <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10"
            />

            {/* Swipe/Drag Layer for Laptop */}
            <motion.div
              className="absolute inset-0 z-40 cursor-grab active:cursor-grabbing"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />

            <LaptopFrame className="w-full max-w-[320px] md:max-w-[420px]">
              <div className="w-full h-full relative bg-black overflow-hidden group/laptop">
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                  <motion.div
                    key={currentImageIndex}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="absolute inset-0 w-full h-full"
                  >
                    <motion.img
                      src={allImages[currentImageIndex]}
                      alt={title}
                      className="w-full h-full object-cover object-top select-none pointer-events-none"
                      draggable={false}
                    />
                    <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(0,0,0,0.5)] pointer-events-none z-10" />
                  </motion.div>
                </AnimatePresence>
                
                {/* Carousel Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20 px-2 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/5 opacity-0 group-hover/laptop:opacity-100 transition-opacity duration-300">
                  {allImages.map((_, idx) => (
                    <div 
                      key={idx}
                      className={cn(
                        "w-1 h-1 rounded-full transition-all duration-300",
                        idx === currentImageIndex ? "bg-white w-3" : "bg-white/30"
                      )}
                    />
                  ))}
                </div>
              </div>
            </LaptopFrame>
          </div>
        ) : (
          <div className="relative w-full h-full overflow-hidden pointer-events-none">
            <motion.div
              className="absolute inset-0 flex items-center justify-center cursor-grab active:cursor-grabbing z-30 pointer-events-auto"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />

            <div className="relative w-full h-full">
              {allImages.map((img, idx) => {
                const position =
                  (idx - currentImageIndex + allImages.length) % (allImages.length || 1);
                const isCenter = position === 0;
                const isLeft = position === allImages.length - 1;
                const isRight = position === 1;
                const isExitingLeft = position === allImages.length - 2;
                const isExitingRight = position === 2;

                return (
                  <motion.div
                    key={idx}
                    className="absolute top-0 w-[48%] md:w-1/3 h-full px-1.5 flex items-center justify-center"
                    style={{ originX: 0.5 }}
                    initial={false}
                    animate={
                      isCenter
                        ? {
                            x: isMobile ? "50%" : "100%",
                            opacity: 1,
                            zIndex: 3,
                            scale: isMobile ? 0.85 : 1,
                            rotate: 0,
                            y: "0%",
                          }
                        : isLeft
                        ? {
                            x: isMobile ? "-15%" : "0%",
                            opacity: 0.8,
                            zIndex: 2,
                            scale: isMobile ? 0.7 : 0.8,
                            rotate: -12,
                            y: "10%",
                          }
                        : isRight
                        ? {
                            x: isMobile ? "123%" : "200%",
                            opacity: 0.8,
                            zIndex: 2,
                            scale: isMobile ? 0.7 : 0.8,
                            rotate: 12,
                            y: "10%",
                          }
                        : isExitingLeft
                        ? {
                            x: "-100%",
                            opacity: 0,
                            zIndex: 1,
                            scale: 0.7,
                          }
                        : isExitingRight
                        ? {
                            x: "300%",
                            opacity: 0,
                            zIndex: 1,
                            scale: 0.7,
                          }
                        : {
                            x: position < allImages.length / 2 ? "300%" : "-100%",
                            opacity: 0,
                            zIndex: 1,
                            scale: 0.7,
                          }
                    }
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 25,
                    }}
                  >
                    <div className="relative w-full max-w-full aspect-[9/19] rounded-[1.25rem] overflow-hidden border-[3px] border-black/80 dark:border-white/20 shadow-2xl shadow-black/50 bg-black">
                      <Image
                        src={img}
                        alt={`${title || "Project"} - ${idx + 1}`}
                        fill
                        unoptimized
                        draggable={false}
                        loading={
                          idx === 0
                            ? undefined
                            : idx === currentImageIndex
                            ? "eager"
                            : "lazy"
                        }
                        quality={85}
                        priority={idx === 0}
                        sizes="(max-width: 768px) 50vw, 33vw"
                        className="object-cover select-none pointer-events-none"
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Memoize with custom comparison to prevent unnecessary re-renders
export const ProjectCard = memo(
  ProjectCardComponent,
  (prevProps, nextProps) => {
    // Return true if props are equal (skip re-render), false if different (re-render)
    return (
      prevProps.id === nextProps.id &&
      prevProps.title === nextProps.title &&
      prevProps.year === nextProps.year &&
      prevProps.description === nextProps.description &&
      prevProps.image === nextProps.image &&
      prevProps.githubUrl === nextProps.githubUrl &&
      prevProps.liveUrl === nextProps.liveUrl &&
      JSON.stringify(prevProps.images) === JSON.stringify(nextProps.images) &&
      JSON.stringify(prevProps.tags) === JSON.stringify(nextProps.tags) &&
      prevProps.link === nextProps.link
    );
  }
);
